import { Button } from "@/components/ui/button";
import { AlertTriangle, RefreshCw } from "lucide-react";
import React, { ErrorInfo, ReactNode, useCallback } from "react";
import { ErrorBoundary as ReactErrorBoundary } from "react-error-boundary";

/**
 * Determines if an error should be filtered out from Sentry reporting
 * to avoid noise from expected/common errors
 */
function isExpectedError(error: Error): boolean {
  const message = error.message.toLowerCase();
  const stack = error.stack?.toLowerCase() || "";

  // Filter out common browser/network errors that aren't actionable
  // Starting with empty array to report all errors to Sentry initially
  // Add specific error patterns here as needed to reduce noise
  const expectedPatterns: string[] = [];

  // Check if error matches expected patterns
  return expectedPatterns.some((pattern) => message.includes(pattern) || stack.includes(pattern));
}

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

/**
 * Global error boundary for handling unexpected errors in the application
 *
 * Features:
 * - Catches JavaScript errors anywhere in the component tree
 * - Shows simple fallback UI for catastrophic failures
 * - Logs error details in development
 * - Integrates with monitoring services
 */
export function ErrorBoundary({ children, fallback, onError }: Props) {
  const handleError = useCallback(
    (caught: unknown, errorInfo: ErrorInfo) => {
      // react-error-boundary hands us `unknown`, since a component can throw
      // any value. Normalize so the rest of this handler has a real Error.
      const error = caught instanceof Error ? caught : new Error(String(caught));

      // Log error details
      console.error("Error caught by boundary:", error, errorInfo);

      // Call custom error handler if provided
      onError?.(error, errorInfo);

      // Send error to monitoring service in production
      if (process.env.NODE_ENV === "production" && window.Sentry) {
        // Filter out common/expected errors that don't need monitoring
        const shouldReport = !isExpectedError(error);
        if (shouldReport) {
          window.Sentry.captureException(error, {
            extra: errorInfo,
            tags: {
              component: "ErrorBoundary",
              errorBoundary: "true",
            },
            level: "error",
          });
        }
      }
    },
    [onError],
  );

  const fallbackRender = useCallback(() => {
    // If custom fallback is provided, use it
    if (fallback) {
      return fallback;
    }

    // Simple fallback UI for catastrophic failures
    return <ErrorFallback />;
  }, [fallback]);

  return (
    <ReactErrorBoundary onError={handleError} fallbackRender={fallbackRender}>
      {children}
    </ReactErrorBoundary>
  );
}

function ErrorFallback() {
  return (
    <div className="flex flex-col items-center justify-center min-h-screen text-center space-y-4">
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
        <AlertTriangle className="h-6 w-6 text-destructive" />
      </div>

      <div className="space-y-2">
        <h2 className="text-xl font-semibold">Something went wrong</h2>
        <p className="text-muted-foreground max-w-md">
          Reloading the page will often resolve issues like this.
        </p>
      </div>

      <Button onClick={() => window.location.reload()} variant="outline">
        <RefreshCw className="h-4 w-4" />
        Reload Page
      </Button>

      <p className="text-muted-foreground pt-4">
        If the error persists, please contact support@teeba-calculator.com.
      </p>
    </div>
  );
}

/**
 * Hook for handling async errors in functional components
 * Custom implementation for throwing errors to the boundary
 */
export function useErrorHandler() {
  return useCallback((error: Error) => {
    // Throw the error to be caught by the nearest error boundary
    throw error;
  }, []);
}

/**
 * Higher-order component wrapper for Error Boundary
 */
export function withErrorBoundary<P extends object>(
  Component: React.ComponentType<P>,
  errorBoundaryProps?: Omit<Props, "children">,
) {
  const WrappedComponent = (props: P) => (
    <ErrorBoundary {...errorBoundaryProps}>
      <Component {...props} />
    </ErrorBoundary>
  );

  WrappedComponent.displayName = `withErrorBoundary(${Component.displayName || Component.name})`;

  return WrappedComponent;
}
